import React, { useState, useEffect, useCallback } from "react";
import { Box, Chip, IconButton, Grid, Typography } from "@mui/material";
import { toast } from "@/components/toastify";
import CustomCard from "@/pages/leave/components/CustomCard";
import { Form, Formik } from "formik";
import dayjs from "dayjs";
import { format } from 'date-fns';
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import { StyledButton } from "@/components";
import { useGetAttendanceReportQuery } from "@/api/services/attendance/attendanceReport/AttendnceReport";
import moment from "moment";
import CommonDataTable from "@/components/dataTableComponents/NewDatatablePagination";
import { DownloadIcon } from "@/icons";
import * as XLSX from 'xlsx';


function RegularizationReport() {

    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    // const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "MM-yyyy"));
    const [selectedMonth, setSelectedMonth] = useState(moment().format("MM-YYYY"));

    const { data: rowDetails = [], refetch, isLoading, isUninitialized } = useGetAttendanceReportQuery({
        month: selectedMonth,
        page: currentPage,
        size: pageSize,
        sortDirection: "DESC",
        search: searchText
    }, { skip: !selectedMonth });
    const rows = rowDetails?.data?.content || [];
    const totalRows = rowDetails?.data?.totalElements || 0;
    const pageCount = rowDetails?.data?.totalPages || Math.ceil(totalRows / pageSize);

    const refetchData = async () => {
        setLoading(true);
        try {
            await refetch();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isUninitialized) {
            refetchData();
        }
    }, [selectedMonth, currentPage, pageSize, searchText, refetch, isUninitialized]);

    const columns = [
        { field: "empId", headerName: "Employee Id", flex: 1 },
        { field: "empName", headerName: "Employee Name", flex: 1 },
        { field: "status", headerName: "Status", flex: 1 },
        { field: "attendanceDate", headerName: "Attendance Date", flex: 1 },
        { field: "actualIn", headerName: "Actual First In", flex: 1 },
        { field: "actualOut", headerName: "Actual Last Out", flex: 1 },
        { field: "appliedIn", headerName: "Applied First In", flex: 1 },
        { field: "appliedOut", headerName: "Applied Last Out", flex: 1 },
        // { field: "regularizationStatus", headerName: "Work Type", flex: 1 },
        {
  field: "regularizationStatus",
  headerName: "Work Type",
  flex: 1,
  renderCell: (params) => {
    const map = {
      REG: "Regularization",
      WFH: "Work From Home",
      OD: "On Duty",
    };
    return <>{map[params?.value] || "-"}</>;
  },
},
        { field: "remark", headerName: "Remarks", flex: 1 },
        { field: "reportingManagerId", headerName: "Reporting Manager Id", flex: 1 },
        { field: "reportingManagerName", headerName: "Reporting Manager", flex: 1 },
        // { field: "reviwerRemark", headerName: "Reviewer Remarks", flex: 1 },
    ];

    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((newSize) => {
        setPageSize(newSize);
        setCurrentPage(0);
    }, []);

    const handleSearchChange = (value) => {
        setSearchText(value);
        setCurrentPage(0);  // Reset to first page on new search
    };

    // Reset pagination when month changes
    useEffect(() => {
        setCurrentPage(0);
    }, [selectedMonth]);

    const downloadExcel = () => {
        // Create a new workbook and a worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, "Regularization Report");

        // Generate a binary string representation of the workbook
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Create a Blob from the buffer
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

        // Create a download link and trigger the download
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'RegularizationReport.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Formik
                initialValues={{ startDate: selectedMonth ? moment(selectedMonth, "MM-YYYY").format("MMMM YYYY") : "" }}
                onSubmit={(values) => {
                    setSelectedMonth(values.startDate);
                    setDataTableKey((prevKey) => prevKey + 1);
                }}
            >
                {({ setFieldValue, values, resetForm }) => (
                    <Form>
                        <Grid container alignItems="center" spacing={2} justifyContent="space-between">
                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                {/* <Box sx={{ display: "flex", gap: 2 }}> */}
                                <FormikMonthYearPicker
                                    name="startDate"
                                    label="Month Year"
                                    pickerType="month"
                                    placeholder="Select"
                                    // value={values.startDate}
                                    value={values.startDate ? moment(values.startDate, "MM-YYYY").format("MMMM YYYY") : ""}
                                    onChange={(formattedDate) => {
                                        const formatted = moment(formattedDate).format("MM-YYYY");
                                        setFieldValue("startDate", formatted);
                                        setSelectedMonth(formatted); // Update state
                                        //   setDataTableKey((prevKey) => prevKey + 1);
                                    }}
                                    maxDate={new Date()}
                                />
                                {/* </Box> */}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} lg={3} >
                                {/* download onsite attendance excel */}
                                <StyledButton
                                    icon={<DownloadIcon />}
                                    label="Download Excel"
                                    onClick={() => downloadExcel("download.xlsx")}
                                    size="small"
                                    variant="outlined"
                                    type="button"
                                    disabled={
                                        !rows || rows.length === 0
                                    }
                                    sx={{
                                        mt: 1,
                                        borderRadius: "4px",
                                        color: "hsl(38,92%,50%)",
                                        background: "rgb(253,239,214)",
                                        border: "1px solid hsl(38,92%,50%)",
                                        "&:hover": {
                                            color: "hsl(38,92%,50%)",
                                            background: "rgb(253,239,214)",
                                            border: "1px solid hsl(38,92%,50%)",
                                        },
                                        ...( // if disabled, override styles
                                            (!rows || rows.length === 0) && {
                                                color: "#aaa",
                                                background: "#f0f0f0",
                                                border: "1px solid #ccc",
                                                cursor: "not-allowed",
                                            }
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} container justifyContent="flex-end">
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <StyledButton
                                        sx={{
                                            p: (theme) => theme.spacing(1, 2),
                                            m: (theme) => theme.spacing(0, 1),
                                            borderRadius: (theme) => theme.shape.s,
                                            height: 40,
                                        }}
                                        type="reset"
                                        label="Reset"
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => { resetForm(), setSelectedMonth(moment().format("MM-YYYY")), setCurrentPage(0), setSearchText(""); }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>

            <CommonDataTable
                columns={columns}
                rows={rows}
                checkbox={false}
                search={true}
                loading={loading || isLoading}
                onSearchChange={handleSearchChange}
                pageCount={pageCount}
                totalRecords={totalRows}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                currentPage={currentPage}
                currentPageSize={pageSize}
                searchValue={searchText}
            />
        </>
    );
};

export default RegularizationReport;

